/**
 * SDMS Import Files → the sheets OG refreshes from its in-memory rowset after an import.
 *
 * OG imports INTO `qcbatchinfo.dtsample` (the merge rowset): every matched row takes the file's
 * values AND `SheetName` = the imported file's name, and the workbook is then re-bound from that
 * rowset. So after an import OG's "Sequence" sheet shows each run's DataFile (SHEETNAME), AcqTime
 * (ANALYZEDDATE) and the Batch Data Path header — its Sequence dataset is a DISTINCT over the
 * rowset (:3551), so a run whose rows now carry two AcqTimes lists twice (OG QB26092507_060 LOQ1) —
 * and, for a MultipleInstruments template, its "Raw Data" summary is the DISTINCT
 * (SysSampleCode, SheetName) of that rowset (BufferRawDataForImport :31808 → AttachRawData :31819).
 *
 * G6 writes an import straight into the live sheet cells, so neither sheet moved (user report
 * 2026-09-28: "Sequence sheet data is incorrect", "Raw Data sheet is missing"). This module keeps
 * the same rowset view: it applies each import to a COPY of the merge records (never the loaded
 * records themselves), from which the page re-merges the Sequence sheet and rebuilds the summary.
 */
import { loadSdmsWorkbook, settleAddedSheet, writeSdmsWorkbook } from './sdmsWorkbookIo';

type Row = Record<string, unknown>;

/** One imported file: its name and the rows the server resolved for it. */
export interface ImportedFileWrites {
  fileName: string;
  writes: ReadonlyArray<{
    sysSampleCode: string;
    parameter: string;
    fields: ReadonlyArray<{ field: string; value: string | null }>;
    dilution?: string | null;
  }>;
}

/** The accumulated import state for one loaded batch: per record index, the fields the imports set. */
export type ImportOverlay = Map<number, Record<string, string | null>>;

const up = (s: unknown) => String(s ?? '').trim().toUpperCase();

/** The parser's ISO-shaped date (`yyyy-MM-dd[ HH:mm[:ss]]`, the YAML spec's output format) → the one
 *  date text the merge turns into a real date (`MM/DD/YYYY[ HH:mm[:ss]]`, canonicalDateText), so a
 *  date-formatted cell such as the Sequence AcqTime holds a DATE as in OG, not text. Else unchanged. */
export function canonicalImportValue(v: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec((v ?? '').trim());
  if (!m) return v;
  const date = `${m[2]}/${m[3]}/${m[1]}`;
  return m[4] ? `${date} ${m[4]}:${m[5]}${m[6] ? `:${m[6]}` : ''}` : date;
}
const keyOf = (rec: Row, name: string): string | undefined =>
  Object.keys(rec).find(k => k.toUpperCase().replace(/^\[(.+)\]$/, '$1') === name);
const get = (rec: Row, name: string): unknown => { const k = keyOf(rec, name); return k ? rec[k] : undefined; };

/**
 * Fold `files` (in import order — a later file overwrites an earlier one's value, as OG) into
 * `overlay`, matching each write to the records by SYSSAMPLECODE + PARAMETER (+ DILUTION when the
 * write carries one). A matched record also takes SHEETNAME = the file name. Returns the number of
 * records touched. Unmatched writes are ignored (they matched no band row on the sheet either).
 */
export function applyImportToOverlay(records: readonly Row[], overlay: ImportOverlay, files: readonly ImportedFileWrites[]): number {
  const index = new Map<string, number[]>();
  records.forEach((r, i) => {
    const k = `${up(get(r, 'SYSSAMPLECODE'))}|${up(get(r, 'PARAMETER'))}`;
    const list = index.get(k); if (list) list.push(i); else index.set(k, [i]);
  });
  const touched = new Set<number>();
  for (const f of files) {
    for (const w of f.writes) {
      const hits = index.get(`${up(w.sysSampleCode)}|${up(w.parameter)}`) ?? [];
      for (const i of hits) {
        if (w.dilution != null && w.dilution !== '' && up(get(records[i]!, 'DILUTION')) !== up(w.dilution)) continue;
        const o = overlay.get(i) ?? {};
        for (const fv of w.fields) o[fv.field.toUpperCase()] = canonicalImportValue(fv.value);
        o.SHEETNAME = f.fileName;
        overlay.set(i, o);
        touched.add(i);
      }
    }
  }
  return touched.size;
}

/** The records with the overlay applied — new objects, the originals untouched. An overlay field
 *  replaces the record's own column of the same name (case-insensitive), else is added. */
export function recordsWithImport(records: readonly Row[], overlay: ImportOverlay): Row[] {
  return records.map((r, i) => {
    const o = overlay.get(i);
    if (!o) return r;
    const out: Row = { ...r };
    for (const [field, value] of Object.entries(o)) out[keyOf(out, field) ?? field] = value;
    return out;
  });
}

/** One imported value to persist on Save (see importValuesToSave). */
export interface SdmsImportValue { sysSampleCode: string; parameter: string; runNo: string | null; field: string; value: string }

/**
 * The import values to send with a Save. OG's save copies every non-null column of its rowset onto the
 * SampleInfo row (SpreadsheetEntryController :16882), so a value an import gave a row is stored even when
 * no sheet cell shows it — VOC's USERDEFINED3 instrument response (OG vs AI VOC comparison 2026-09-28,
 * defect 7). One entry per overlay field, carrying the record's RUNNO so a re-run's row is the one written.
 * A BLANK value is sent as '': the file listed that analyte without a value, and OG's rowset holds '' there
 * (not DBNull), so its save stores '' — VOC's USERDEFINED3 on 290 rows (comparison 2026-09-29). SHEETNAME is
 * left out; the server also skips locators and the fields the sheet owns.
 */
export function importValuesToSave(records: readonly Row[], overlay: ImportOverlay): SdmsImportValue[] {
  const out: SdmsImportValue[] = [];
  for (const [i, o] of overlay) {
    const r = records[i];
    if (!r) continue;
    const sysSampleCode = String(get(r, 'SYSSAMPLECODE') ?? '').trim();
    const parameter = String(get(r, 'PARAMETER') ?? '').trim();
    if (!sysSampleCode || !parameter) continue;
    const run = String(get(r, 'RUNNO') ?? '').trim();
    for (const [field, value] of Object.entries(o)) {
      if (field === 'SHEETNAME') continue;
      out.push({ sysSampleCode, parameter, runNo: run || null, field, value: value == null || value.trim() === '' ? '' : value });
    }
  }
  return out;
}

/** OG's "Raw Data" summary rows: DISTINCT (SysSampleCode, SheetName) over the rowset, rows with a
 *  SheetName only, in rowset (sequence) order (:31807-31808). */
export function rawDataSummaryRows(records: readonly Row[]): Array<{ sysSampleCode: string; fileName: string }> {
  const seen = new Set<string>();
  const out: Array<{ sysSampleCode: string; fileName: string }> = [];
  for (const r of records) {
    const code = String(get(r, 'SYSSAMPLECODE') ?? '').trim();
    const file = String(get(r, 'SHEETNAME') ?? '').trim();
    if (!code || !file) continue;
    const k = `${code.toUpperCase()}|${file.toUpperCase()}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ sysSampleCode: code, fileName: file });
  }
  return out;
}

/**
 * The template's sheets, other than the result sheets, that show one of `fields` as a FIELD("…") / [[…]] token.
 * OG re-binds the whole workbook from its rowset after an import, so these show the imported values too — THM's
 * "Summary2" carries the Batch Data Path header (USERDEFINED2), each run's response (USERDEFINED3) and the Report
 * Time (ANALYZEDDATE), all blank / stale in G6 (OG vs AI THM comparison 2026-09-29). The page re-merges them like
 * the Sequence sheet. `isResult` is the page's result-sheet test (by name and position).
 */
export async function nonResultSheetsShowingFields(
  templateBuf: ArrayBuffer, fields: ReadonlySet<string>, isResult: (name: string, index: number) => boolean,
): Promise<string[]> {
  if (!fields.size) return [];
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(templateBuf.slice(0));
  const bare = (n: string) => n.trim().toUpperCase().replace(/^\[(.+)\]$/, '$1');
  const out: string[] = [];
  wb.worksheets.forEach((ws, index) => {
    if (isResult(ws.name, index)) return;
    let shows = false;
    ws.eachRow({ includeEmpty: false }, row => {
      if (shows) return;
      row.eachCell({ includeEmpty: false }, c => {
        if (shows) return;
        const text = typeof c.formula === 'string' ? c.formula : typeof c.value === 'string' ? c.value : '';
        for (const m of text.matchAll(/FIELD\("([^"]*)"\)|\[\[([^\]]+)\]\]/gi))
          if (fields.has(bare(m[1] ?? m[2] ?? ''))) { shows = true; break; }
      });
    });
    if (shows) out.push(ws.name);
  });
  return out;
}

/**
 * Replace the `sheetName` worksheet of `liveBuf` with the one in `sourceBuf` (a fresh merge), keeping
 * its tab position. Returns the new buffer, or null when either workbook lacks the sheet.
 */
export async function replaceSheetFrom(liveBuf: ArrayBuffer, sourceBuf: ArrayBuffer, sheetName: string): Promise<ArrayBuffer | null> {
  const { copySheet } = await import('./sdmsMailMerge');
  const live = await loadSdmsWorkbook(liveBuf);
  const src = await loadSdmsWorkbook(sourceBuf);
  return replaceSheetIn(live, src, sheetName, copySheet) ? writeSdmsWorkbook(live) : null;
}

type Workbook = import('exceljs').Workbook;
type Worksheet = import('exceljs').Worksheet;

/** replaceSheetFrom's edit, in memory: returns the fresh sheet, or null (nothing changed) when either lacks it. */
function replaceSheetIn(live: Workbook, src: Workbook, sheetName: string,
  copySheet: (from: Worksheet, to: Worksheet) => void): Worksheet | null {
  const from = src.getWorksheet(sheetName);
  const old = live.getWorksheet(sheetName);
  if (!from || !old) return null;
  const order = (old as unknown as { orderNo: number }).orderNo;
  const state = old.state;
  const fresh = live.addWorksheet(`${sheetName}__new`);
  copySheet(from, fresh);
  live.removeWorksheet(old.id);
  fresh.name = sheetName;
  fresh.state = state;
  (fresh as unknown as { orderNo: number }).orderNo = order;
  return fresh;
}

/**
 * The import's sheet refresh in ONE pass (speed, 2026-09-30): each of `sheetNames` replaced from `sourceBuf`
 * (the re-merge with the imported values), then the multi-instrument "Raw Data" summary — what one
 * replaceSheetFrom per sheet followed by mergeRawDataSummary did, but with one parse of each workbook and one
 * write instead of a parse of both and a write per sheet (~1.6 s each on a VOC batch). A sheet replaced before
 * the last change is given the print defaults the next step's re-read gave it (settleAddedSheet), so the
 * bytes are the same. Returns `liveBuf` itself when nothing changed.
 */
export async function refreshImportedSheets(
  liveBuf: ArrayBuffer, sourceBuf: ArrayBuffer | null, sheetNames: readonly string[],
  summary: { rows: ReadonlyArray<{ sysSampleCode: string; fileName: string }>; template: ArrayBuffer | null } | null,
): Promise<ArrayBuffer> {
  const { copySheet } = await import('./sdmsMailMerge');
  const { mergeRawDataSummaryIntoWorkbook } = await import('./sdmsAttachRawData');
  const live = await loadSdmsWorkbook(liveBuf);
  const src = sourceBuf ? await loadSdmsWorkbook(sourceBuf) : null;
  let wrote = false;
  let pending: Worksheet[] = [];
  const changed = (added: Worksheet[]) => { pending.forEach(settleAddedSheet); pending = added; wrote = true; };
  if (src) for (const name of sheetNames) { const fresh = replaceSheetIn(live, src, name, copySheet); if (fresh) changed([fresh]); }
  if (summary?.rows.length) {
    const before = new Set(live.worksheets);
    if (await mergeRawDataSummaryIntoWorkbook(live, summary.rows, summary.template)) changed(live.worksheets.filter(w => !before.has(w)));
  }
  return wrote ? writeSdmsWorkbook(live) : liveBuf;
}
